import { useMemo } from "react";

import { mergeCategoryFiltersConfigs } from "app/pages/sales-intelligence/modules/advanced-search-composition/utils/filterConfig";
import { allCategoryConfig } from "app/pages/sales-intelligence/modules/advanced-search-filtering/configs/filterCategory";
import type { IFiltersConfigCategoryItem } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/category";
import { filterUsageMap } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterDefinition";
import type { FiltersConfig } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filtersConfig";
import { useCrmCustomFiltersListViewModel } from "app/pages/sales-intelligence/modules/crm-integration/hooks/useCrmCustomFiltersListViewModel";
import { createLeadsFiltersSearchConfig } from "app/pages/sales-intelligence/modules/leads-search-filters/configs/createLeadsFiltersSearchConfig";
import { useComputeCrmFiltersConfig } from "app/pages/sales-intelligence/modules/leads-search-filters/hooks/useComputeCrmFiltersConfig";
import { useComputeLeadsFiltersConfig } from "app/pages/sales-intelligence/modules/leads-search-filters/hooks/useComputeLeadsFiltersConfig";
import { useLeadsGrants } from "app/pages/sales-intelligence/modules/leads-search-filters/hooks/useLeadsGrants";
import {
    type LeadsFilterCategory,
    leadsFilterCategoryMap,
} from "app/pages/sales-intelligence/modules/leads-search-filters/types/leadsFilterCategory";
import type { OrderedFilterInstance } from "app/pages/sales-intelligence/modules/leads-search-filters/types/leadsSearchFilterInstance";

// keywords / internationalFilters / leadScores opt out of the "scoring-rule" usage (see their definitions),
// so the scoring surface's usage drops them — no post-filter needed.
const SCORING_DESIRED_CATEGORIES = new Set<LeadsFilterCategory>([
    leadsFilterCategoryMap.signals,
    leadsFilterCategoryMap.company,
    leadsFilterCategoryMap.website,
    leadsFilterCategoryMap.trafficAndEngagement,
    leadsFilterCategoryMap.advertiserActivity,
]);

type UseScoringFiltersConfigResult = FiltersConfig<readonly OrderedFilterInstance[]> & {
    readonly filtersSearchConfigList: ReturnType<typeof createLeadsFiltersSearchConfig>[];
    readonly categoriesList: readonly IFiltersConfigCategoryItem[];
};

export const useScoringFiltersConfig = (): UseScoringFiltersConfigResult => {
    const crmCustomFiltersListViewModel = useCrmCustomFiltersListViewModel();

    const leadsGrants = useLeadsGrants({
        isExcludeSectionAvailable: true,
        isCrmScopeFilterAvailable: true,
        activeCrmIntegration: crmCustomFiltersListViewModel.integrationObject?.integrationName,
    });

    const leadsSelection = useMemo(
        () => ({
            usage: filterUsageMap.scoringRule,
            categories: SCORING_DESIRED_CATEGORIES,
            grants: leadsGrants,
        }),
        [leadsGrants],
    );

    const leadsResult = useComputeLeadsFiltersConfig({ selection: leadsSelection });

    const crmResult = useComputeCrmFiltersConfig(crmCustomFiltersListViewModel, { grants: leadsGrants });

    const filtersSearchConfigList = useMemo(() => [createLeadsFiltersSearchConfig()], []);

    const merged = useMemo(
        () => mergeCategoryFiltersConfigs<OrderedFilterInstance>(allCategoryConfig, leadsResult, crmResult),
        [
            leadsResult.status,
            leadsResult.filtersList,
            leadsResult.categoriesList,
            crmResult.status,
            crmResult.filtersList,
            crmResult.categoriesList,
        ],
    );

    return {
        filtersList: merged.filtersList,
        status: merged.status,
        filtersSearchConfigList,
        categoriesList: merged.categoriesList,
    };
};
